import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE, fmtDate, mediaUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Receipt, Plus, Upload, Paperclip, Trash2, Pencil, FileText, CheckCircle, AlertTriangle, Search, X, User, Sparkles, Loader2, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const CATEGORIES = ["fuel", "maintenance", "insurance", "dockage", "provisions", "supplies", "services", "commercial", "other"];
const STATUS_BADGE = { paid: "badge-approved", unpaid: "badge-pending", overdue: "badge-rejected" };
const CAT_BADGE = {
  fuel: "badge-pending", maintenance: "badge-fishing", insurance: "badge-protected",
  dockage: "badge-neutral", provisions: "badge-neutral", supplies: "badge-neutral",
  services: "badge-neutral", commercial: "badge-approved", other: "badge-neutral",
};

export default function Invoices() {
  const { user } = useAuth();
  const t = useT();
  const [invoices, setInvoices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "all", category: "all", paid_by: "all", q: "" });
  const [editing, setEditing] = useState(null);
  const [snapOnOpen, setSnapOnOpen] = useState(false);

  const canManage = ["super_admin", "owner_director", "operations_admin", "accountant", "maintenance_user"].includes(user?.role);
  const canDelete = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);

  // Honour the ?snap=1 deep-link from the Dashboard "Snap receipt" shortcut.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("snap") === "1" && canManage) {
      setSnapOnOpen(true);
      setEditing({});
      // Clear the param so a refresh doesn't auto-trigger again.
      const next = new URLSearchParams(searchParams);
      next.delete("snap");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canManage]);

  const reload = async () => {
    setLoading(true);
    const params = {};
    if (filter.status !== "all") params.status = filter.status;
    if (filter.category !== "all") params.category = filter.category;
    if (filter.paid_by !== "all") params.paid_by = filter.paid_by;
    if (filter.q) params.q = filter.q;
    const [inv, a, s, u] = await Promise.all([
      api.get("/invoices", { params }),
      api.get("/assets"),
      api.get("/invoices/summary"),
      api.get("/users"),
    ]);
    setInvoices(inv.data); setAssets(a.data); setSummary(s.data); setUsers(u.data); setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter.status, filter.category, filter.paid_by]);

  const markPaid = async (id) => {
    try { await api.patch(`/invoices/${id}`, { status: "paid" }); toast.success(t("invoices.markPaid")); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("invoices.confirmDelete"))) return;
    try { await api.delete(`/invoices/${id}`); toast.success(t("common.delete")); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("invoices.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("invoices.title")}</h2>
          <p className="text-sm text-[#94A3B8] mt-1">{t("invoices.intro")}</p>
        </div>
        {canManage && (
          <Button onClick={() => setEditing({})} data-testid="new-invoice-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            <Plus className="w-4 h-4 mr-1" /> {t("invoices.new")}
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label={t("invoices.stat.total")} value={`€ ${summary.total.toFixed(0)}`} sub={t("invoices.stat.totalSub", { count: summary.count })} tone="blue" icon={Receipt} />
          <Stat label={t("invoices.stat.outstanding")} value={`€ ${summary.unpaid_total.toFixed(0)}`} sub={t("invoices.stat.outstandingSub")} tone="amber" icon={AlertTriangle} />
          <Stat label={t("invoices.stat.overdue")} value={summary.overdue_count} sub={t("invoices.stat.overdueSub")} tone="red" icon={AlertTriangle} />
          <Stat label={t("invoices.stat.categories")} value={Object.keys(summary.by_category).length} sub={t("invoices.stat.categoriesSub")} tone="emerald" icon={FileText} />
        </div>
      )}

      {summary && summary.by_payer && summary.by_payer.length > 0 && (
        <div className="ledger-card p-5" data-testid="invoice-by-payer">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-[#4DA6FF]" />
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("invoices.perPayer")}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.by_payer.map(p => {
              const share = summary.total > 0 ? (p.total / summary.total * 100) : 0;
              const isUnassigned = !p.user_id;
              return (
                <div key={p.user_id || "unassigned"}
                  className={`p-4 rounded-md border ${isUnassigned ? "bg-[#050A14] border-[#1E293B]" : "bg-[#050A14] border-[#1E293B] hover:border-[#4DA6FF]"} transition-colors`}
                  data-testid={`payer-card-${p.user_id || "unassigned"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display font-bold text-sm">{isUnassigned ? t("invoices.unassigned") : p.name}</div>
                    <div className="font-mono text-[10px] text-[#94A3B8]">{share.toFixed(0)}%</div>
                  </div>
                  <div className="font-display text-xl font-bold tracking-tight">€ {p.total.toFixed(0)}</div>
                  <div className="mt-2 flex justify-between text-[11px] font-mono">
                    <span className="text-[#34D399]">{t("invoices.paidShort")} € {p.paid_total.toFixed(0)}</span>
                    <span className="text-[#FBBF24]">{t("invoices.unpaidShort")} € {p.unpaid_total.toFixed(0)}</span>
                  </div>
                  <div className="font-mono text-[10px] text-[#94A3B8] mt-1">{t("invoices.stat.totalSub", { count: p.count })}</div>
                  <div className="mt-2 h-1 bg-[#1E293B] rounded-full overflow-hidden">
                    <div className="h-full bg-[#4DA6FF]" style={{ width: `${share}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="ledger-card p-4 flex flex-wrap gap-3 items-end">
        <form onSubmit={(e) => { e.preventDefault(); reload(); }} className="flex-1 min-w-[220px]">
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("places.searchLabel")}</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={filter.q}
              onChange={(e) => setFilter(f => ({...f, q: e.target.value}))}
              placeholder={t("invoices.searchPlaceholder")}
              data-testid="invoice-search"
              className="bg-[#050A14] border-[#1E293B] pl-9"
            />
            {filter.q && (
              <button type="button" onClick={() => { setFilter(f => ({...f, q: ""})); setTimeout(reload, 0); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8]"><X className="w-4 h-4" /></button>
            )}
          </div>
        </form>
        <div>
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("invoices.filter.status")}</Label>
          <select value={filter.status} onChange={(e) => setFilter(f => ({...f, status: e.target.value}))}
            data-testid="invoice-status-filter"
            className="mt-1 bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
            <option value="all">{t("common.all")}</option>
            <option value="unpaid">{t("status.unpaid")}</option>
            <option value="overdue">{t("status.overdue")}</option>
            <option value="paid">{t("status.paid")}</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("invoices.filter.category")}</Label>
          <select value={filter.category} onChange={(e) => setFilter(f => ({...f, category: e.target.value}))}
            data-testid="invoice-category-filter"
            className="mt-1 bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
            <option value="all">{t("common.all")}</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{t(`invoices.categories.${c}`)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("invoices.filter.paidBy")}</Label>
          <select value={filter.paid_by} onChange={(e) => setFilter(f => ({...f, paid_by: e.target.value}))}
            data-testid="invoice-paidby-filter"
            className="mt-1 bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
            <option value="all">{t("common.all")}</option>
            {users.filter(u => u.role === "owner_director").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            {users.filter(u => u.role !== "owner_director").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <Button type="button" onClick={reload} data-testid="invoice-apply-filter" className="bg-[#007AFF] hover:bg-[#0056B3]">{t("common.apply")}</Button>
      </div>

      {loading ? <div className="h-64 ledger-card animate-pulse" /> :
        invoices.length === 0 ? (
          <div className="ledger-card p-12 text-center text-[#94A3B8]">
            <Receipt className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <div className="text-sm">{t("invoices.empty")}</div>
          </div>
        ) : (
          <div className="ledger-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#070D1B] border-b border-[#1E293B]">
                  <tr className="font-mono text-[10px] tracking-widest text-[#94A3B8]">
                    <th className="text-left px-4 py-3">{t("invoices.col.date")}</th>
                    <th className="text-left px-4 py-3">{t("invoices.col.vendor")}</th>
                    <th className="text-left px-4 py-3">{t("invoices.col.description")}</th>
                    <th className="text-left px-4 py-3">{t("invoices.col.category")}</th>
                    <th className="text-left px-4 py-3">{t("invoices.col.paidBy")}</th>
                    <th className="text-right px-4 py-3">{t("invoices.col.amount")}</th>
                    <th className="text-center px-4 py-3">{t("invoices.col.status")}</th>
                    <th className="text-right px-4 py-3">{t("invoices.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-[#1E293B] hover:bg-[#131C31]" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono text-[11px] text-[#94A3B8]">{fmtDate(inv.invoice_date)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{inv.vendor}</div>
                        {inv.invoice_number && <div className="font-mono text-[10px] text-[#94A3B8]">#{inv.invoice_number}</div>}
                      </td>
                      <td className="px-4 py-3 max-w-sm">
                        <div className="text-xs truncate">{inv.description || "—"}</div>
                        {inv.asset_id && <div className="font-mono text-[10px] text-[#94A3B8]">{assets.find(a => a.id === inv.asset_id)?.name}</div>}
                      </td>
                      <td className="px-4 py-3"><span className={`badge-pill ${CAT_BADGE[inv.category] || "badge-neutral"}`}>{t(`invoices.categories.${inv.category}`)}</span></td>
                      <td className="px-4 py-3">
                        {inv.paid_by_name ? (
                          <span className="text-xs font-medium">{inv.paid_by_name}</span>
                        ) : (
                          <span className="font-mono text-[10px] text-[#94A3B8]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-medium">{inv.currency} {inv.amount.toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`badge-pill ${STATUS_BADGE[inv.status] || "badge-neutral"}`}>{t(`status.${inv.status}`)}</span>
                        {inv.due_date && inv.status !== "paid" && <div className="font-mono text-[9px] text-[#94A3B8] mt-1">{t("invoices.due", { date: fmtDate(inv.due_date) })}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          {inv.file_id && (
                            <a href={mediaUrl(inv.file_id)} target="_blank" rel="noreferrer"
                              data-testid={`invoice-attach-${inv.id}`}
                              className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#4DA6FF] text-[#4DA6FF]" title={t("invoices.openAttachment")}>
                              <Paperclip className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {canManage && inv.status !== "paid" && (
                            <button onClick={() => markPaid(inv.id)} data-testid={`invoice-pay-${inv.id}`}
                              className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#10B981] text-[#34D399]" title={t("invoices.markPaid")}>
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canManage && (
                            <button onClick={() => setEditing(inv)} data-testid={`invoice-edit-${inv.id}`}
                              className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#4DA6FF] text-[#94A3B8]" title={t("common.edit")}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => remove(inv.id)} data-testid={`invoice-delete-${inv.id}`}
                              className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#EF4444] text-[#F87171]" title={t("common.delete")}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      {editing !== null && <InvoiceDialog editing={editing.id ? editing : null} assets={assets} autoSnap={snapOnOpen} onClose={() => { setEditing(null); setSnapOnOpen(false); }} onSaved={() => { setEditing(null); setSnapOnOpen(false); reload(); }} />}
    </div>
  );
}

function Stat({ label, value, sub, tone, icon: Icon }) {
  const toneMap = {
    blue: "text-[#4DA6FF] border-[#007AFF]/30 bg-[#007AFF]/10",
    amber: "text-[#FBBF24] border-[#F59E0B]/30 bg-[#F59E0B]/10",
    red: "text-[#F87171] border-[#EF4444]/30 bg-[#EF4444]/10",
    emerald: "text-[#34D399] border-[#10B981]/30 bg-[#10B981]/10",
  };
  return (
    <div className="ledger-card p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${toneMap[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-mono text-[10px] tracking-widest text-[#94A3B8]">{label}</div>
      <div className="font-display text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-[#94A3B8] mt-1">{sub}</div>}
    </div>
  );
}

function InvoiceDialog({ editing, assets, onClose, onSaved, autoSnap = false }) {
  const t = useT();
  const isEdit = !!editing;
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(editing ? {
    invoice_number: editing.invoice_number || "",
    vendor: editing.vendor || "",
    amount: editing.amount ?? "",
    currency: editing.currency || "EUR",
    invoice_date: editing.invoice_date || new Date().toISOString().slice(0, 10),
    due_date: editing.due_date || "",
    category: editing.category || "other",
    asset_id: editing.asset_id || "",
    status: editing.status || "unpaid",
    description: editing.description || "",
    file_id: editing.file_id || null,
    paid_by_user_id: editing.paid_by_user_id || "",
  } : {
    invoice_number: "", vendor: "", amount: "", currency: "EUR",
    invoice_date: new Date().toISOString().slice(0, 10), due_date: "",
    category: "other", asset_id: "", status: "unpaid", description: "", file_id: null,
    paid_by_user_id: "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef();
  const scanRef = useRef();
  const cameraScanRef = useRef();

  // When opened via the Snap-receipt shortcut, auto-trigger the camera capture
  // on first paint. iOS Safari requires this to happen inside the user-gesture
  // chain that opened the dialog; React's commit phase preserves it.
  useEffect(() => {
    if (autoSnap) {
      // Defer one tick so the input is mounted in the DOM.
      const timer = setTimeout(() => { cameraScanRef.current?.click(); }, 50);
      return () => clearTimeout(timer);
    }
  }, [autoSnap]);

  const handleScan = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/invoices/parse-image", fd);
      // Map parser fields → form fields. Don't overwrite manual edits unless they're empty.
      setForm(prev => ({
        ...prev,
        vendor: data.vendor || prev.vendor,
        invoice_number: data.invoice_number || prev.invoice_number,
        amount: data.amount != null ? String(data.amount) : prev.amount,
        currency: data.currency || prev.currency,
        invoice_date: data.invoice_date || prev.invoice_date,
        due_date: data.due_date || prev.due_date,
        category: (data.category && CATEGORIES.includes(data.category)) ? data.category : prev.category,
        description: data.description || prev.description,
      }));
      // Attach the scanned file as the invoice attachment automatically
      setFile(f);
      toast.success(t("invoices.scanSuccess"));
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  };

  useEffect(() => { (async () => {
    const { data } = await api.get("/users"); setUsers(data);
  })(); }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.vendor) { toast.error("Vendor required"); return; }
    if (form.amount === "" || isNaN(parseFloat(form.amount))) { toast.error("Amount required"); return; }
    setSaving(true);
    try {
      let file_id = form.file_id;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("linked_type", "invoice");
        if (editing?.id) fd.append("linked_id", editing.id);
        const { data } = await api.post("/media/upload", fd);
        file_id = data.id;
      }
      const body = {
        invoice_number: form.invoice_number || null,
        vendor: form.vendor,
        amount: parseFloat(form.amount),
        currency: form.currency || "EUR",
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        category: form.category,
        asset_id: form.asset_id || null,
        status: form.status,
        description: form.description || null,
        file_id,
        paid_by_user_id: form.paid_by_user_id || null,
      };
      // strip nulls for POST (unpaid status allowed) but for PATCH we want to send status
      Object.keys(body).forEach(k => { if (body[k] === null || body[k] === "") delete body[k]; });
      // ensure required fields are present on create
      if (!isEdit) {
        body.vendor = form.vendor; body.amount = parseFloat(form.amount);
        body.invoice_date = form.invoice_date; body.category = form.category; body.status = form.status; body.currency = form.currency;
      }
      if (isEdit) {
        await api.patch(`/invoices/${editing.id}`, body);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", body);
        toast.success("Invoice logged");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-invoice-dialog" : "new-invoice-dialog"}>
        <DialogHeader><DialogTitle className="font-display tracking-tight">{isEdit ? t("invoices.editDialog.title") : t("invoices.newDialog.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* AI scan banner — only on create, since edit already has data */}
          {!isEdit && (
            <div className="rounded-md border border-[#A78BFA]/40 bg-gradient-to-br from-[#8B5CF6]/10 to-[#4DA6FF]/10 p-3 flex items-center gap-3" data-testid="invoice-scan-banner">
              <Sparkles className="w-5 h-5 text-[#A78BFA] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] tracking-widest text-[#A78BFA]">{t("invoices.scan.label")}</div>
                <div className="text-xs text-[#94A3B8]">{t("invoices.scan.help")}</div>
              </div>
              <input
                ref={scanRef} type="file" accept="image/*,.heic,.heif,application/pdf"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={handleScan} data-testid="invoice-scan-input"
              />
              <input
                ref={cameraScanRef} type="file" accept="image/*" capture="environment"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={handleScan} data-testid="invoice-scan-camera-input"
              />
              <Button
                type="button" disabled={scanning} onClick={() => cameraScanRef.current?.click()}
                data-testid="invoice-scan-camera-btn"
                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-xs h-9 shrink-0">
                <Camera className="w-3 h-3 mr-1" />{t("invoices.scan.snap")}
              </Button>
              <Button
                type="button" disabled={scanning} onClick={() => scanRef.current?.click()}
                data-testid="invoice-scan-btn"
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs h-9 shrink-0">
                {scanning ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{t("invoices.scan.scanning")}</> : <><Sparkles className="w-3 h-3 mr-1" />{t("invoices.scan.btn")}</>}
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <F label={t("invoices.dialog.vendor")}><Input value={form.vendor} onChange={(e) => upd("vendor", e.target.value)} placeholder="Marina Piran" data-testid="inv-vendor" className="bg-[#050A14] border-[#1E293B]" /></F>
            <F label={t("invoices.dialog.invoiceNum")}><Input value={form.invoice_number} onChange={(e) => upd("invoice_number", e.target.value)} placeholder="2026-0425" data-testid="inv-number" className="bg-[#050A14] border-[#1E293B]" /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label={t("invoices.dialog.amount")}><Input type="number" step="0.01" value={form.amount} onChange={(e) => upd("amount", e.target.value)} data-testid="inv-amount" className="bg-[#050A14] border-[#1E293B]" /></F>
            <F label={t("invoices.dialog.currency")}>
              <select value={form.currency} onChange={(e) => upd("currency", e.target.value)}
                data-testid="inv-currency"
                className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
                <option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option>
              </select>
            </F>
            <F label={t("invoices.dialog.date")}><Input type="date" value={form.invoice_date} onChange={(e) => upd("invoice_date", e.target.value)} data-testid="inv-date" className="bg-[#050A14] border-[#1E293B]" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("invoices.dialog.category")}>
              <select value={form.category} onChange={(e) => upd("category", e.target.value)}
                data-testid="inv-category"
                className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm capitalize">
                {CATEGORIES.map(c => <option key={c} value={c}>{t(`invoices.categories.${c}`)}</option>)}
              </select>
            </F>
            <F label={t("invoices.dialog.status")}>
              <select value={form.status} onChange={(e) => upd("status", e.target.value)}
                data-testid="inv-status"
                className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
                <option value="unpaid">{t("status.unpaid")}</option><option value="paid">{t("status.paid")}</option><option value="overdue">{t("status.overdue")}</option>
              </select>
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("invoices.dialog.dueDate")}><Input type="date" value={form.due_date} onChange={(e) => upd("due_date", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></F>
            <F label={t("invoices.dialog.asset")}>
              <select value={form.asset_id} onChange={(e) => upd("asset_id", e.target.value)}
                data-testid="inv-asset"
                className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
                <option value="">{t("common.none")}</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </F>
          </div>
          <F label={t("invoices.dialog.paidBy")}>
            <select value={form.paid_by_user_id} onChange={(e) => upd("paid_by_user_id", e.target.value)}
              data-testid="inv-paid-by"
              className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              <option value="">{t("invoices.dialog.paidByNone")}</option>
              <optgroup label={t("invoices.dialog.owners")}>
                {users.filter(u => u.role === "owner_director").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </optgroup>
              <optgroup label={t("invoices.dialog.team")}>
                {users.filter(u => u.role !== "owner_director").map(u => {
                  const label = `${u.name} (${t(`role.${u.role}`)})`;
                  return <option key={u.id} value={u.id}>{label}</option>;
                })}
              </optgroup>
            </select>
          </F>
          <F label={t("invoices.dialog.description")}><Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder={t("invoices.dialog.descPlaceholder")} data-testid="inv-description" className="bg-[#050A14] border-[#1E293B]" rows={3} /></F>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("invoices.dialog.attachment")}</Label>
            <div
              onClick={() => inputRef.current?.click()}
              className="mt-1 border border-dashed border-[#1E293B] rounded-md p-3 text-center cursor-pointer hover:border-[#4DA6FF]"
              data-testid="inv-file-dropzone"
            >
              <Upload className="w-5 h-5 mx-auto mb-1 text-[#94A3B8]" />
              <div className="text-xs text-[#94A3B8]">
                {file ? file.name : form.file_id ? t("invoices.dialog.attachExists") : t("invoices.dialog.attachHint")}
              </div>
              <input ref={inputRef} type="file" accept="image/*,application/pdf"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={(e) => setFile(e.target.files[0])}
                data-testid="inv-file-input"
              />
            </div>
            {form.file_id && !file && (
              <a href={mediaUrl(form.file_id)} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#4DA6FF] hover:underline">
                <Paperclip className="w-3 h-3" /> {t("invoices.dialog.viewExisting")}
              </a>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="inv-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            {saving ? t("common.saving") : (isEdit ? t("common.saveChanges") : t("invoices.new"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{label}</Label><div className="mt-1">{children}</div></div>;
}
